import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PropTypes from 'prop-types';
const CategorySlider = ({ categories = [], selectedCategory, onCategoryClick }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const hasOverflow = container.scrollWidth > container.clientWidth;
    const currentScrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    setShowLeftButton(hasOverflow && currentScrollLeft > 5);
    setShowRightButton(hasOverflow && currentScrollLeft < maxScroll - 5);
  };

  useEffect(() => {
    if (!categories || categories.length === 0) return;
    
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, [categories]);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Multiply by 2 for faster scroll
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 200;
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });

    setTimeout(checkScrollButtons, 300);
  };

  // Return null or empty div if no categories
  if (!categories || categories.length === 0) {
    return null;
  }

  return (
    <div className="position-relative">
      <div className="d-flex align-items-center">
        {showLeftButton && (
          <button
            onClick={() => scroll('left')}
            className="btn btn-sm btn-light border-0 shadow-sm position-absolute start-0 z-index-1"
            style={{
              width: '32px',
              height: '32px',
              padding: '0',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '-8px',
              zIndex: 10
            }}
          >
            <ChevronLeft size={18} />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          onScroll={checkScrollButtons}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          className="d-flex gap-2 overflow-auto"
          style={{
            scrollBehavior: isDragging ? 'auto' : 'smooth',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            padding: '4px 0',
            cursor: 'grab',
            userSelect: 'none'
          }}
        >
          <style>
            {`
              .overflow-auto::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`btn btn-sm flex-shrink-0 ${
                selectedCategory === category.id ? 'btn-primary' : 'btn-outline-primary'
              }`}
              onClick={() => onCategoryClick(category.id)}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                padding: '6px 16px',
                fontSize: '14px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                pointerEvents: isDragging ? 'none' : 'auto'
              }}
            >
              {category.name}
            </button>
          ))}
        </div>

        {showRightButton && (
          <button
            onClick={() => scroll('right')}
            className="btn btn-sm btn-light border-0 shadow-sm position-absolute end-0 z-index-1"
            style={{
              width: '32px',
              height: '32px',
              padding: '0',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '-8px',
              zIndex: 10
            }}
          >
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
};
CategorySlider.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  selectedCategory: PropTypes.string,
  onCategoryClick: PropTypes.func.isRequired,
};

CategorySlider.defaultProps = {
  categories: [],
  selectedCategory: null,
};
export default CategorySlider;